<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  useToast,
} from '@manta/ui-kit';
import ClientSearchPanel from '@/components/ClientPicker/ClientSearchPanel.vue';
import ClientDetailPanel from '@/components/ClientPicker/ClientDetailPanel.vue';
import ClientFormPanel from '@/components/ClientPicker/ClientFormPanel.vue';
import { useClient } from '@/api/clients/useClient';
import { useClientSearch } from '@/api/clients/useClientSearch';
import { useClientPatients } from '@/api/clients/useClientPatients';
import type { CreateClientWithPatientsResult } from '@/api/clients/useClientCreateWithPatients';
import { useClientRecents } from '@/composables/useClientRecents';
import { animalsOf, clientDisplayName, clientSelectionOf } from '@/lib/clients';
import type {
  AnimalSnapshot,
  ClientSelection,
  MantaClient,
  MantaPatient,
} from '@/types';

const props = defineProps<{
  /** Prefill when the session already has a customer so its details open first. */
  initial?: {
    clientId: string;
    patientIds: string[];
    clientName?: string;
    patients?: AnimalSnapshot[];
  } | null;
  /** Open on mount for a blank session; the user can dismiss it. */
  autoOpen?: boolean;
}>();

const emit = defineEmits<{
  select: [payload: ClientSelection];
}>();

const { t } = useI18n();
const { toast } = useToast();
const { recents, remember, update: updateRecent } = useClientRecents();

type View = 'search' | 'detail' | 'create' | 'edit';

const open = ref(false);
const didAutoOpen = ref(false);
const view = ref<View>('search');
const query = ref('');
const contentEl = ref<HTMLElement | null>(null);

const selectedClient = ref<MantaClient | null>(null);
const selectedIds = ref<Set<string>>(new Set());
/** Animals created here or known from the session snapshot. */
const extraAnimals = ref<MantaPatient[]>([]);
/** Edits applied here, shown before the refetched list catches up. */
const animalOverrides = ref<Record<string, MantaPatient>>({});

const { data: results, isFetching: searching } = useClientSearch(query);
const clientId = computed(() => selectedClient.value?.id);
const inClientView = computed(() => open.value && view.value !== 'search');
const { data: fetchedClient } = useClient(clientId, inClientView);
const { data: fetchedAnimals, isFetching: animalsFetching } = useClientPatients(
  clientId,
  inClientView,
);

const client = computed<MantaClient | null>(() => {
  const base = selectedClient.value;
  if (!base) return null;
  const fetched = fetchedClient.value;
  if (fetched?.id !== base.id) return base;
  return { ...base, ...fetched, patientLinks: base.patientLinks };
});

const animals = computed<MantaPatient[]>(() => {
  const merged = [
    ...(fetchedAnimals.value ?? []),
    ...(selectedClient.value ? animalsOf(selectedClient.value) : []),
    ...extraAnimals.value,
  ];
  const seen = new Set<string>();
  return merged.flatMap((animal) => {
    if (seen.has(animal.id)) return [];
    seen.add(animal.id);
    return [{ ...animal, ...animalOverrides.value[animal.id] }];
  });
});

function animalsFromInitial(): MantaPatient[] {
  const seen = new Set<string>();
  const fromSnapshot = (props.initial?.patients ?? []).flatMap((patient) => {
    if (!patient.id || seen.has(patient.id)) return [];
    seen.add(patient.id);
    return [
      {
        id: patient.id,
        name: patient.name,
        species: patient.species,
        breed: patient.breed,
      },
    ];
  });
  const fromIds = (props.initial?.patientIds ?? []).flatMap((id) => {
    if (seen.has(id)) return [];
    seen.add(id);
    return [{ id }];
  });
  return [...fromSnapshot, ...fromIds];
}

watch(
  () => props.autoOpen,
  (shouldOpen) => {
    if (!shouldOpen || didAutoOpen.value || props.initial?.clientId) return;
    didAutoOpen.value = true;
    open.value = true;
  },
  { immediate: true },
);

watch(open, (isOpen) => {
  if (!isOpen) return;
  query.value = '';
  animalOverrides.value = {};
  if (props.initial?.clientId) {
    selectedClient.value = {
      id: props.initial.clientId,
      display_name: props.initial.clientName,
    };
    selectedIds.value = new Set(props.initial.patientIds);
    extraAnimals.value = animalsFromInitial();
    view.value = 'detail';
    return;
  }
  selectedClient.value = null;
  selectedIds.value = new Set();
  extraAnimals.value = [];
  view.value = 'search';
});

const FOCUSABLE = 'input, button, textarea, select';

function focusView() {
  void nextTick(() => {
    // ui-kit's Button wraps the <button>, so the marker can sit on a wrapper.
    const marked =
      contentEl.value?.querySelector<HTMLElement>('[data-autofocus]');
    const target = marked?.matches(FOCUSABLE)
      ? marked
      : marked?.querySelector<HTMLElement>(FOCUSABLE);
    target?.focus();
  });
}

watch(view, focusView);

function onOpenAutoFocus(event: Event) {
  event.preventDefault();
  focusView();
}

function applySelection(payload: ClientSelection) {
  remember(payload);
  emit('select', payload);
  open.value = false;
}

function openClient(next: MantaClient) {
  if (selectedClient.value?.id !== next.id) {
    const nextAnimals = animalsOf(next);
    selectedIds.value = new Set(
      nextAnimals.length === 1 ? [nextAnimals[0].id] : [],
    );
    extraAnimals.value = [];
    animalOverrides.value = {};
  }
  selectedClient.value = next;
  view.value = 'detail';
}

function toggleAnimal(id: string) {
  const next = new Set(selectedIds.value);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  selectedIds.value = next;
}

function confirm() {
  if (!client.value) return;
  const chosen = animals.value.filter((a) => selectedIds.value.has(a.id));
  applySelection(clientSelectionOf(client.value, chosen));
}

/**
 * Keeps the session's stored snapshot and the recents list in step with an
 * edit, without closing the picker or changing which animals are attached.
 */
function syncSnapshots() {
  const current = client.value;
  if (!current) return;
  const name = clientDisplayName(current) || undefined;
  const byId = new Map(animals.value.map((a) => [a.id, a]));
  const refreshPatients = (patients: AnimalSnapshot[] = []) =>
    patients.map((p) => {
      const fresh = p.id ? byId.get(p.id) : undefined;
      return fresh
        ? {
            ...p,
            name: fresh.name ?? undefined,
            species: fresh.species ?? undefined,
          }
        : p;
    });

  updateRecent(current.id, (item) => ({
    ...item,
    snapshot: {
      ...item.snapshot,
      client_name: name,
      patients: refreshPatients(item.snapshot.patients),
    },
  }));

  if (props.initial?.clientId !== current.id) return;
  const attachedIds = new Set(props.initial.patientIds);
  emit(
    'select',
    clientSelectionOf(
      current,
      animals.value.filter((a) => attachedIds.has(a.id)),
    ),
  );
}

function onClientSaved(updated: MantaClient) {
  if (selectedClient.value) {
    selectedClient.value = {
      ...selectedClient.value,
      ...updated,
      patientLinks: selectedClient.value.patientLinks,
    };
  }
  view.value = 'detail';
  syncSnapshots();
}

function onClientCreated({
  selection,
  failedPatients,
}: CreateClientWithPatientsResult) {
  if (failedPatients.length) {
    toast({
      title: t('workspace.animalsPartialError', {
        names: failedPatients.join(', '),
      }),
      variant: 'destructive',
    });
  }
  applySelection(selection);
}

function onAnimalCreated(animal: MantaPatient) {
  extraAnimals.value = [...extraAnimals.value, animal];
  selectedIds.value = new Set([...selectedIds.value, animal.id]);
}

function onAnimalUpdated(animal: MantaPatient) {
  animalOverrides.value = { ...animalOverrides.value, [animal.id]: animal };
  syncSnapshots();
}
</script>

<template>
  <Popover v-model:open="open">
    <PopoverTrigger as-child>
      <slot />
    </PopoverTrigger>
    <PopoverContent
      align="start"
      class="w-[400px] max-w-[calc(100vw-1rem)] overflow-hidden rounded-xl p-0 shadow-lg"
      @open-auto-focus="onOpenAutoFocus"
    >
      <div ref="contentEl" class="flex flex-col">
        <ClientSearchPanel
          v-if="view === 'search'"
          v-model:query="query"
          :results="results ?? []"
          :recents="recents"
          :loading="searching"
          @open-client="openClient"
          @pick-animal="(c, a) => applySelection(clientSelectionOf(c, [a]))"
          @pick-recent="applySelection"
          @create="view = 'create'"
          @skip="open = false"
        />
        <ClientDetailPanel
          v-else-if="view === 'detail' && client"
          :client="client"
          :animals="animals"
          :selected-ids="selectedIds"
          :animals-loading="animalsFetching"
          @back="view = 'search'"
          @edit="view = 'edit'"
          @toggle="toggleAnimal"
          @confirm="confirm"
          @animal-created="onAnimalCreated"
          @animal-updated="onAnimalUpdated"
        />
        <ClientFormPanel
          v-else-if="view === 'edit' && client"
          mode="edit"
          :client="client"
          @back="view = 'detail'"
          @saved="onClientSaved"
        />
        <ClientFormPanel
          v-else-if="view === 'create'"
          mode="create"
          :initial-name="query.trim()"
          @back="view = 'search'"
          @created="onClientCreated"
        />
      </div>
    </PopoverContent>
  </Popover>
</template>
